import React from "react";
import MenuItem from "@mui/material/MenuItem";
import Menu from "@mui/material/Menu";
import Divider from "@mui/material/Divider";
import ListItemIcon from "@mui/material/ListItemIcon";
import Share from "@mui/icons-material/Share";
import Bookmark from "@mui/icons-material/Bookmark";
import Book from "@mui/icons-material/Book";

interface MenuPagesProps {
  isOpen: boolean;
  anchorEl: HTMLElement | null; // allow null when closed
  onClose: () => void;
  isSharingAvailable: boolean;
  isShowBookmarkEntries: boolean;
  chapterNumber?: number; // (unused here, kept for API compatibility)
  bookmarkClicked?: () => void;
  gotoBookmarkClicked?: () => void;
  shareVersesClicked?: () => void;
  onNewUrl: (theUrl: string) => void;
}

const PAGES = [
  ["download", "Download"],
  ["books", "Books"],
  ["about", "About"],
  ["contact", "Contact"],
] as const;

function MenuPagesImpl(props: MenuPagesProps): JSX.Element {
  const {
    isOpen,
    anchorEl,
    onClose,
    isSharingAvailable,
    isShowBookmarkEntries,
    bookmarkClicked,
    gotoBookmarkClicked,
    shareVersesClicked,
    onNewUrl,
  } = props;

  // Single handler for all static page items (avoids N closures)
  const handlePagesMenuItemClick = React.useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      const page = (event.currentTarget as HTMLElement).dataset.page;
      if (!page) return;
      onNewUrl("/" + page);
    },
    [onNewUrl],
  );

  return (
    <Menu
      id="pages-menu"
      anchorEl={anchorEl}
      open={isOpen}
      onClose={onClose}
      aria-labelledby="pages-menu-button"
      keepMounted // keep portal DOM to avoid mount cost on each open
      transitionDuration={0} // disable grow/fade animation for quicker paint
      // If you prefer centered positioning, uncomment:
      // anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      // transformOrigin={{ vertical: "top", horizontal: "center" }}
    >
      {PAGES.map(([key, label]) => (
        <MenuItem key={key} data-page={key} onClick={handlePagesMenuItemClick} disableRipple>
          {label}
        </MenuItem>
      ))}

      <Divider />

      {isShowBookmarkEntries && (
        <>
          <MenuItem onClick={bookmarkClicked} disableRipple>
            <ListItemIcon>
              <Book fontSize="small" />
            </ListItemIcon>
            Bookmark Verse
          </MenuItem>
          <MenuItem onClick={gotoBookmarkClicked} disableRipple>
            <ListItemIcon>
              <Bookmark fontSize="small" />
            </ListItemIcon>
            Go To Bookmark
          </MenuItem>
        </>
      )}

      {isSharingAvailable && (
        <>
          <Divider />
          <MenuItem onClick={shareVersesClicked} disableRipple>
            <ListItemIcon>
              <Share fontSize="small" />
            </ListItemIcon>
            Share Verses
          </MenuItem>
        </>
      )}

      <Divider />

      <MenuItem component="a" href="https://www.quranfor.com/kids">
        Quran for Kids
      </MenuItem>

      <MenuItem component="a" href="https://www.quranfor.com/teens">
        Quran for Teens
      </MenuItem>

    </Menu>
  );
}

// Re-render only if relevant props change
export default React.memo(
  MenuPagesImpl,
  (a, b) =>
    a.isOpen === b.isOpen &&
    a.anchorEl === b.anchorEl &&
    a.isSharingAvailable === b.isSharingAvailable &&
    a.isShowBookmarkEntries === b.isShowBookmarkEntries &&
    a.bookmarkClicked === b.bookmarkClicked &&
    a.gotoBookmarkClicked === b.gotoBookmarkClicked &&
    a.shareVersesClicked === b.shareVersesClicked &&
    a.onNewUrl === b.onNewUrl,
);
